import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { RotateCcw } from 'lucide-react';
import api from '../../lib/api';
import { C } from '../../lib/payrollHelpers';
import { Input } from '../ui/input';

export function AttendanceChangesTab() {
  const [data, setData] = useState({ items: [], total: 0, clinicians: [] });
  const [filters, setFilters] = useState({ clinician: '', status: '', dos_from: '', dos_to: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    api.get('/attendance-changes', { params }).then(({ data }) => setData(data)).catch(() => toast.error('Could not load changes'));
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const restore = async (it) => {
    if (!window.confirm(`Restore ${it.client} · ${it.dos} · ${it.status} as a live row? It will count again in payroll and reports, and future uploads won't remove it.`)) return;
    setBusy(true);
    try {
      await api.post(`/attendance-changes/${encodeURIComponent(it.row_id)}/restore`);
      toast.success('Row restored');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Restore failed');
    } finally { setBusy(false); }
  };

  const set = (k) => (e) => setFilters(f => ({ ...f, [k]: e.target.value }));

  return (
    <div className="space-y-3" data-testid="pr-sp-changes-tab">
      <p className="text-[12px]" style={{ color: C.soft }}>
        Attendance rows that were in an earlier upload but are missing from the latest one for the same dates — the appointment's status was changed or it was removed in SimplePractice. They're excluded from payroll, overpayment checks and reports. Undo puts a row back.
      </p>
      <div className="flex flex-wrap gap-2 items-center">
        <select value={filters.clinician} onChange={set('clinician')} className="h-9 rounded-md border px-2 text-sm" style={{ borderColor: C.border }} data-testid="pr-spc-filter-clinician">
          <option value="">All therapists</option>
          {data.clinicians.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filters.status} onChange={set('status')} className="h-9 rounded-md border px-2 text-sm" style={{ borderColor: C.border }} data-testid="pr-spc-filter-status">
          <option value="">All statuses</option>
          {['Show', 'Late canceled', 'No show', 'Canceled', 'Clinician canceled'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <Input type="date" value={filters.dos_from} onChange={set('dos_from')} className="w-40 h-9" data-testid="pr-spc-filter-from" />
        <Input type="date" value={filters.dos_to} onChange={set('dos_to')} className="w-40 h-9" data-testid="pr-spc-filter-to" />
        <span className="text-[12px] ml-auto" style={{ color: C.soft }} data-testid="pr-spc-total">{data.total} row{data.total === 1 ? '' : 's'}</span>
      </div>
      <div className="rounded-lg border overflow-x-auto" style={{ borderColor: C.border }}>
        <table className="w-full text-[12px]">
          <thead style={{ background: '#F4F0F7', color: C.ink }}>
            <tr>
              {['Therapist', 'Client', 'DOS', 'Was', 'Now in SP', 'Detected', 'Upload', ''].map(h => <th key={h} className="text-left px-3 py-2">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.items.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-6 text-center" style={{ color: C.soft }} data-testid="pr-spc-empty">No changed or removed rows.</td></tr>
            ) : data.items.map(it => (
              <tr key={it.row_id} className="border-t" style={{ borderColor: C.border }} data-testid={`pr-spc-row-${it.row_id}`}>
                <td className="px-3 py-2">{it.clinician}</td>
                <td className="px-3 py-2 font-semibold" style={{ color: C.ink }}>{it.client}</td>
                <td className="px-3 py-2 tabular-nums">{it.dos}</td>
                <td className="px-3 py-2">{it.status}</td>
                <td className="px-3 py-2">{(it.now_statuses || []).join(', ') || 'Removed'}</td>
                <td className="px-3 py-2 tabular-nums" style={{ color: C.soft }}>{(it.changed_at || '').slice(0, 10)}</td>
                <td className="px-3 py-2 text-[11px]" style={{ color: C.soft }}>{it.changed_by_upload}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => restore(it)} disabled={busy} className="px-2 py-1 text-[11px] rounded border hover:bg-[#F1EEF4]" style={{ borderColor: C.plum, color: C.plum }} data-testid={`pr-spc-undo-${it.row_id}`}>
                    <RotateCcw size={10} className="inline mr-0.5" /> Undo
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
